/* eslint-disable max-lines */
import { isDefined, isNil } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { API_TYPES } from "@src/appV2/api";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import {
  PLACEMENT_COHORT,
  type PlacementCandidate,
} from "@src/appV2/redesign/PlacementCandidate/types";
import { augmentYearsOfExperienceWithWorkerLicenses } from "@src/appV2/redesign/PlacementCandidate/utils";
import { useCreateWorkerFavoritePlacement } from "@src/appV2/redesign/Placements/api/useCreateWorkerFavoritePlacement";
import { useDeleteWorkerFavoritePlacement } from "@src/appV2/redesign/Placements/api/useDeleteWorkerFavoritePlacement";
import { usePayTransparencyRates } from "@src/appV2/redesign/Placements/lib/usePayTransparencyRates";
import { constructPlacementDataFromWorkerFavoriteResponse } from "@src/appV2/redesign/Placements/utils/worker-favorite-placements";
import { Pill, Text } from "@src/appV2/redesign/system/ui-components";
import { type Worker } from "@src/appV2/Worker/api/types";
import { compareAsc, differenceInDays, parseISO } from "date-fns";
import { Fragment, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";

import { useCredentialRequirementConfiguration } from "../../Documents/useCredentialRequirementConfiguration";
import {
  InterviewStatus,
  transformResponseIntoInterview,
  useFetchPaginatedInterviews,
} from "../../WorkerShifts/api/useFetchPaginatedInterviews";
import {
  transformResponseIntoPlacement,
  useFetchPaginatedPlacements,
} from "../api/useFetchPaginatedPlacements";
import { useFetchPlacementApplications } from "../api/useFetchPlacementApplications";
import { useGetEasySchedulingPlacementApplications } from "../api/useGetEasySchedulingPlacementApplications";
import {
  EASY_SCHEDULING_BOOKING_DEADLINE_DAYS,
  useGetEasySchedulingPlacements,
} from "../api/useGetEasySchedulingPlacements";
import { useGetUpcomingInterviewApplications } from "../api/useGetUpcomingInterviewApplications";
import { useGetWorkerFavoritePlacements } from "../api/useGetWorkerFavoritePlacements";
import { usePlacementCohorts } from "../hooks/usePlacementCohorts";
import {
  type GetPlacementsForWorkplaceQuery,
  PLACEMENT_STATUS_MAP,
  PlacementSortOption,
} from "../types/fetchPlacements.schema";
import { placementWithDetailsToData } from "../utils/placement-data";
import { transformPlacementApplicationToPlacementWithDetails } from "../utils/transformPlacementApplicationResponse";
import {
  applicationHasUpcomingInterview,
  extractInterviewsFromPlacementApplications,
} from "../utils/transformPlacementApplicationToInterviewWithDetails";
import { AppliedPlacementCard } from "./AppliedPlacementCard/AppliedPlacementCard";
import { InterviewAttendanceFeedbackCard } from "./InterviewAttendanceFeedback/InterviewAttendanceFeedbackCard";
import { InterviewsAccordionSection } from "./InterviewsAccordionSection/InterviewsAccordionSection";
import { PendingInterviewsSection } from "./PendingInterviewsSection/PendingInterviewsSection";
import { PlacementsAccordionSectionV3 } from "./PlacementAccordionSection/PlacementsAccordionSectionV3";
import { PlacementCardV2 } from "./PlacementCard/PlacementCardV2";
import { useGetPlacementFilters } from "./PlacementDetailsPage/hooks/useGetPlacementFilters";
import { SentApplicationsAccordionSection } from "./SentApplicationsAccordionSection/SentApplicationsAccordionSection";

interface AppliedPlacementListViewProps {
  placementCandidate: PlacementCandidate;
  worker: Worker;
  filter: GetPlacementsForWorkplaceQuery["filter"];
  showApplications: boolean;
}

export function AppliedPlacementListView(props: AppliedPlacementListViewProps) {
  const { placementCandidate, worker, filter, showApplications } = props;

  const isPlacementApplicationsMigrationEnabled = useCbhFlag(
    CbhFeatureFlag.PLACEMENT_APPLICATIONS_MIGRATION,
    { defaultValue: false }
  );

  const isEasySchedulingEnabled = isDefined(worker.userId) && isDefined(placementCandidate.id);

  const {
    data: easySchedulingData,
    isLoading: isEasySchedulingLoading,
    fetchNextPage: fetchNextEasySchedulingPage,
    hasNextPage: hasNextEasySchedulingPage,
    isFetchingNextPage: isFetchingNextEasySchedulingPage,
  } = useGetEasySchedulingPlacements(
    {
      workerId: worker.userId,
      placementCandidateId: placementCandidate.id,
      shouldLogEvent: false,
      additionalFilters: {
        includeClosedPlacementsKeepingInterviewsAndCandidates: true,
      },
    },
    {
      enabled: !isPlacementApplicationsMigrationEnabled && isEasySchedulingEnabled,
    }
  );

  const {
    data: easySchedulingApplicationsData,
    isLoading: isEasySchedulingApplicationsLoading,
    fetchNextPage: fetchNextEasySchedulingApplicationsPage,
    hasNextPage: hasNextEasySchedulingApplicationsPage,
    isFetchingNextPage: isFetchingNextEasySchedulingApplicationsPage,
  } = useGetEasySchedulingPlacementApplications(
    {
      workerId: worker.userId,
    },
    {
      enabled: isPlacementApplicationsMigrationEnabled && isEasySchedulingEnabled,
    }
  );

  const activeIsEasySchedulingLoading = isPlacementApplicationsMigrationEnabled
    ? isEasySchedulingApplicationsLoading
    : isEasySchedulingLoading;
  const activeFetchNextEasySchedulingPage = isPlacementApplicationsMigrationEnabled
    ? fetchNextEasySchedulingApplicationsPage
    : fetchNextEasySchedulingPage;
  const activeHasNextEasySchedulingPage = isPlacementApplicationsMigrationEnabled
    ? hasNextEasySchedulingApplicationsPage
    : hasNextEasySchedulingPage;
  const activeIsFetchingNextEasySchedulingPage = isPlacementApplicationsMigrationEnabled
    ? isFetchingNextEasySchedulingApplicationsPage
    : isFetchingNextEasySchedulingPage;

  const credentialRequirementConfiguration = useCredentialRequirementConfiguration();

  const allEasySchedulingPlacements = isPlacementApplicationsMigrationEnabled
    ? (easySchedulingApplicationsData?.pages ?? []).flatMap((page) =>
        page.data.map((item) =>
          transformPlacementApplicationToPlacementWithDetails(item, page.included)
        )
      )
    : (easySchedulingData?.pages ?? []).flatMap((page) =>
        page.data.map((item) =>
          transformResponseIntoPlacement(item, page.included, credentialRequirementConfiguration)
        )
      );

  const easySchedulingPlacementsAfterDeadline = allEasySchedulingPlacements.filter((placement) => {
    const application = placement.applications?.[0];
    if (!application?.attributes.updatedAt) {
      return true;
    }

    const updatedDate = parseISO(application.attributes.updatedAt);
    const now = new Date();
    const daysSinceUpdated = differenceInDays(now, updatedDate);

    return daysSinceUpdated <= EASY_SCHEDULING_BOOKING_DEADLINE_DAYS;
  });

  // The new endpoint does not accept filter[applicationMethod] server-side, so we filter on
  // meta.isEasyScheduling (backend-computed: scheduleInterviews=false + availability configured +
  // feature flag enabled) to match the legacy endpoint's actual behavior.
  const easySchedulingPlacements = isPlacementApplicationsMigrationEnabled
    ? easySchedulingPlacementsAfterDeadline.filter((placement) => placement.isEasyScheduling)
    : easySchedulingPlacementsAfterDeadline;

  const { data: favoritePlacementsData, isLoading: isFavoritePlacementsLoading } =
    useGetWorkerFavoritePlacements(
      {
        workerId: worker.userId,
      },
      {
        enabled: Boolean(isDefined(worker.userId)),
      }
    );

  const {
    isLoading: isLoadingInterviewApplications,
    data: interviewApplicationsData,
    hasNextPage: hasNextPageInterviewApplications,
    isFetchingNextPage: isFetchingNextInterviewApplicationsPage,
    fetchNextPage: fetchNextInterviewApplicationsPage,
    cutoffDate,
  } = useGetUpcomingInterviewApplications(
    {
      workerId: worker.userId,
    },
    {
      enabled:
        isPlacementApplicationsMigrationEnabled && isDefined(worker.userId) && showApplications,
    }
  );

  const {
    isLoading: isLoadingLegacyInterviews,
    data: legacyInterviewsData,
    hasNextPage: hasNextPageLegacyInterviews,
    isFetchingNextPage: isFetchingNextLegacyInterviewsPage,
    fetchNextPage: fetchNextLegacyInterviewsPage,
  } = useFetchPaginatedInterviews(
    {
      filter: {},
      sort: "statusBookedFirst,-start",
      include: "placementCandidateBan",
    },
    {
      enabled:
        !isPlacementApplicationsMigrationEnabled && isDefined(worker.userId) && showApplications,
    }
  );

  const isLoadingInterviews = isPlacementApplicationsMigrationEnabled
    ? isLoadingInterviewApplications
    : isLoadingLegacyInterviews;
  const hasNextPageInterviews = isPlacementApplicationsMigrationEnabled
    ? hasNextPageInterviewApplications
    : hasNextPageLegacyInterviews;
  const isFetchingNextInterviewsPage = isPlacementApplicationsMigrationEnabled
    ? isFetchingNextInterviewApplicationsPage
    : isFetchingNextLegacyInterviewsPage;
  const fetchNextInterviewsPage = isPlacementApplicationsMigrationEnabled
    ? fetchNextInterviewApplicationsPage
    : fetchNextLegacyInterviewsPage;

  const interviews = isPlacementApplicationsMigrationEnabled
    ? (interviewApplicationsData?.pages ?? [])
        .flatMap((page) => extractInterviewsFromPlacementApplications(page.data, page.included))
        .toSorted((a, b) => compareAsc(a.start, b.start))
    : (legacyInterviewsData?.pages ?? []).flatMap((page) =>
        page.data
          .filter((interview) => interview.attributes.status !== InterviewStatus.PENDING)
          .map((interview) => ({
            ...transformResponseIntoInterview(interview, page.included ?? [], worker.tmz ?? ""),
            start: new Date(interview.attributes.start),
            end: new Date(interview.attributes.end),
          }))
      );

  const placementFilters = useGetPlacementFilters({
    filter,
    showApplications,
    worker,
    hasInterviews: false,
    enabled: isDefined(placementCandidate),
  });

  const isSentApplicationsEnabled = isDefined(worker.userId) && showApplications;

  const {
    data: placementsData,
    isLoading: isPlacementsLoading,
    hasNextPage: hasNextPagePlacements,
    isFetchingNextPage: isFetchingNextPlacementsPage,
    fetchNextPage: fetchNextPlacementsPage,
  } = useFetchPaginatedPlacements(
    {
      workerId: worker.userId,
      placementCandidateId: placementCandidate.id,
      filter: placementFilters,
      shouldLogEvent: true,
      sort: PlacementSortOption.APPLICATIONS_APPLIED_DATE_DESC,
    },
    { enabled: !isPlacementApplicationsMigrationEnabled && isSentApplicationsEnabled }
  );

  const {
    data: placementApplicationsData,
    isLoading: isPlacementApplicationsLoading,
    hasNextPage: hasNextPagePlacementApplications,
    isFetchingNextPage: isFetchingNextPlacementApplicationsPage,
    fetchNextPage: fetchNextPlacementApplicationsPage,
  } = useFetchPlacementApplications(
    {
      workerId: worker.userId,
      sort: "desc",
    },
    { enabled: isPlacementApplicationsMigrationEnabled && isSentApplicationsEnabled }
  );

  const activeIsPlacementsLoading = isPlacementApplicationsMigrationEnabled
    ? isPlacementApplicationsLoading
    : isPlacementsLoading;
  const activeHasNextPagePlacements = isPlacementApplicationsMigrationEnabled
    ? hasNextPagePlacementApplications
    : hasNextPagePlacements;
  const activeIsFetchingNextPlacementsPage = isPlacementApplicationsMigrationEnabled
    ? isFetchingNextPlacementApplicationsPage
    : isFetchingNextPlacementsPage;
  const activeFetchNextPlacementsPage = isPlacementApplicationsMigrationEnabled
    ? fetchNextPlacementApplicationsPage
    : fetchNextPlacementsPage;

  const history = useHistory();
  const hasLoggedPageView = useRef(false);
  useEffect(() => {
    if (hasLoggedPageView.current) {
      return;
    }

    const hasData = isPlacementApplicationsMigrationEnabled
      ? isDefined(placementApplicationsData)
      : isDefined(placementsData);

    if (hasData && isSentApplicationsEnabled) {
      hasLoggedPageView.current = true;
      const searchParams = new URLSearchParams(history.location.search);
      logEvent(APP_V2_APP_EVENTS.PLACEMENTS_PAGE_VIEWED, {
        placementCandidateId: placementCandidate.id,
        source: searchParams.get("source"),
      });
    }
  }, [
    isPlacementApplicationsMigrationEnabled,
    placementApplicationsData,
    placementsData,
    isSentApplicationsEnabled,
    placementCandidate.id,
    history.location.search,
  ]);

  const placements = isPlacementApplicationsMigrationEnabled
    ? (placementApplicationsData?.pages ?? []).flatMap((placementPage, pageIndex) =>
        placementPage.data
          .filter(
            (item) => !applicationHasUpcomingInterview(item, placementPage.included, cutoffDate)
          )
          .map((item, placementIndex) => ({
            placementWithDetails: transformPlacementApplicationToPlacementWithDetails(
              item,
              placementPage.included
            ),
            pageIndex,
            placementIndex,
          }))
      )
    : (placementsData?.pages ?? []).flatMap((placementPage, pageIndex) =>
        placementPage.data.map((item, placementIndex) => ({
          placementWithDetails: transformResponseIntoPlacement(
            item,
            placementPage.included,
            credentialRequirementConfiguration
          ),
          pageIndex,
          placementIndex,
        }))
      );

  const { cohorts, flexReviewsInJobs } = usePlacementCohorts();
  const { enabled: isFlexReviewsInJobsEnabled, minFlexRatingsCountToShow } = flexReviewsInJobs;
  const showWorkplaceRatings =
    isFlexReviewsInJobsEnabled && cohorts.includes(PLACEMENT_COHORT.FLEX_REVIEWS_IN_JOBS);

  const { getCachedPayRatesForPlacement, isLoading: isPayDataLoading } = usePayTransparencyRates();

  const { mutateAsync: favoritePlacement, isLoading: isFavoritingPlacement } =
    useCreateWorkerFavoritePlacement();

  const { mutateAsync: unfavoritePlacement, isLoading: isUnfavoritingPlacement } =
    useDeleteWorkerFavoritePlacement();

  return (
    <Stack direction="column" spacing={5} sx={{ pb: 10 }}>
      <InterviewAttendanceFeedbackCard placementCandidate={placementCandidate} />

      {easySchedulingPlacements.length > 0 && !activeIsEasySchedulingLoading && (
        <PlacementsAccordionSectionV3
          initialExpanded
          title={
            <Stack alignItems="center" direction="row" spacing={2}>
              <Text variant="h5">Pending Interview Invites</Text>
              {easySchedulingPlacements.length > 0 ? (
                <Pill
                  label={easySchedulingPlacements.length}
                  size="small"
                  sx={{
                    backgroundColor: (theme) => theme.palette.error.main,
                    color: (theme) => theme.palette.error.contrastText,
                    padding: 0,
                    minHeight: 20,
                    minWidth: 20,
                    height: "max-content",
                    borderRadius: 10,
                  }}
                />
              ) : undefined}
            </Stack>
          }
          isLoading={activeIsEasySchedulingLoading}
          hasNextPage={activeHasNextEasySchedulingPage ?? false}
          isFetchingNextPage={activeIsFetchingNextEasySchedulingPage}
          emptyText={
            <Text variant="body1" sx={{ textAlign: "center" }}>
              You have no pending interview invites at this time.
            </Text>
          }
          totalPlacementsCount={easySchedulingPlacements.length}
          placementIds={easySchedulingPlacements.map((p) => p.id)}
          renderPlacementCard={(placementId, index) => {
            const placement = easySchedulingPlacements.find((p) => p.id === placementId);

            if (!isDefined(placement)) {
              return null;
            }

            return (
              <Fragment key={placement.id}>
                <AppliedPlacementCard
                  placement={placementWithDetailsToData(
                    placement,
                    credentialRequirementConfiguration
                  )}
                  placementCandidate={placementCandidate}
                  pageIndex={0}
                  placementIndex={index}
                  worker={worker}
                  source="easy_scheduling_placements"
                />
              </Fragment>
            );
          }}
          onLoadMore={() => {
            void activeFetchNextEasySchedulingPage();
          }}
        />
      )}

      <InterviewsAccordionSection
        interviews={interviews}
        isLoadingInterviews={isLoadingInterviews}
        hasNextPageInterviews={hasNextPageInterviews ?? false}
        isFetchingNextInterviewsPage={isFetchingNextInterviewsPage}
        showApplications={showApplications}
        placementCandidateId={placementCandidate.id}
        workerTimeZone={worker.tmz}
        onLoadMore={() => {
          void fetchNextInterviewsPage();
        }}
      />

      <PendingInterviewsSection placementCandidate={placementCandidate} worker={worker} />

      <PlacementsAccordionSectionV3
        title="Saved Jobs"
        isLoading={isFavoritePlacementsLoading}
        initialExpanded={(favoritePlacementsData?.included?.length ?? 0) > 0}
        hasNextPage={false}
        isFetchingNextPage={false}
        emptyText={
          <Text variant="body1" sx={{ textAlign: "center" }}>
            You have not saved any jobs to your favorites yet!{" "}
          </Text>
        }
        totalPlacementsCount={
          favoritePlacementsData?.included?.filter(
            (included) => included.type === API_TYPES.placement
          ).length ?? 0
        }
        placementIds={
          favoritePlacementsData?.included
            .filter((included) => included.type === API_TYPES.placement)
            .sort((a, b) => {
              const aIsOpen = a.attributes.status === PLACEMENT_STATUS_MAP.OPEN ? 0 : 1;
              const bIsOpen = b.attributes.status === PLACEMENT_STATUS_MAP.OPEN ? 0 : 1;
              return aIsOpen - bIsOpen;
            })
            .map((placement) => placement.id) ?? []
        }
        renderPlacementCard={(placementId, index) => {
          if (isNil(favoritePlacementsData)) {
            return null;
          }

          const placement = favoritePlacementsData.included
            .filter((included) => included.type === API_TYPES.placement)
            .find((placement) => placement.id === placementId);

          if (!isDefined(placement)) {
            return null;
          }

          const yearsOfExperienceByQualification = augmentYearsOfExperienceWithWorkerLicenses(
            placementCandidate.yearsOfExperienceByQualification,
            worker.licenses
          );

          const { minMaxPayEstimate } = getCachedPayRatesForPlacement(
            {
              id: placement.id,
              workplaceId: placement.relationships.workplace.data.id,
              workerTypes: placement.attributes.workerTypes,
              jobShifts: placement.attributes.jobShifts,
              transparencyPayRates: placement.attributes.transparencyPayRates,
              attributes: { payRange: placement.attributes.payRange },
            },
            yearsOfExperienceByQualification,
            false,
            placementCandidate.payAndScheduleFormData?.benefits?.combined
          );

          const workerFavoritePlacementId = favoritePlacementsData.data.find(
            (fav) => fav.relationships.placement.data.id === placement.id
          )?.id;

          return (
            <PlacementCardV2
              workerQualifications={
                new Set(worker.licensesData.map((license) => license.qualification))
              }
              placement={constructPlacementDataFromWorkerFavoriteResponse(
                placement,
                favoritePlacementsData,
                credentialRequirementConfiguration
              )}
              placementCandidateId={placementCandidate.id}
              isRestricted={placementCandidate.isRestricted ?? false}
              pageIndex={0}
              placementIndex={index}
              loadingPayData={isPayDataLoading}
              showWorkplaceRatings={showWorkplaceRatings}
              minFlexRatingsCountToShow={minFlexRatingsCountToShow}
              minMaxPayEstimate={minMaxPayEstimate}
              source="favorite_placements"
              ongoingFavoritingRequest={isFavoritingPlacement || isUnfavoritingPlacement}
              onFavoriteToggle={async (newFavoriteStatus) => {
                if (isFavoritingPlacement || isUnfavoritingPlacement) {
                  return;
                }

                if (newFavoriteStatus) {
                  await favoritePlacement({
                    workerId: worker.userId,
                    placementId: placement.id,
                    source: "favorite_placements_list",
                  });
                } else if (isDefined(workerFavoritePlacementId)) {
                  await unfavoritePlacement({
                    workerId: worker.userId,
                    workerFavoritePlacementId,
                    placementId: placement.id,
                    source: "favorite_placements_list",
                  });
                }
              }}
            />
          );
        }}
      />

      {placements.length > 0 && !activeIsPlacementsLoading && (
        <SentApplicationsAccordionSection
          placements={placements}
          isLoadingPlacements={activeIsPlacementsLoading}
          hasNextPagePlacements={activeHasNextPagePlacements ?? false}
          isFetchingNextPlacementsPage={activeIsFetchingNextPlacementsPage}
          showApplications={showApplications}
          placementCandidateId={placementCandidate.id}
          workerTimeZone={worker.tmz}
          shouldShowAsApplication={isPlacementApplicationsMigrationEnabled}
          onLoadMore={() => {
            void activeFetchNextPlacementsPage();
          }}
        />
      )}
    </Stack>
  );
}
/* eslint-enable max-lines */
